import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

function AdminSetup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setChecking(false);
        return;
      }
      const adminDoc = await getDoc(doc(db, 'admins', userId));
      setIsAuthorized(adminDoc.exists());
      setChecking(false);
    };
    checkAdmin();
  }, []);

  const setupAdmin = async () => {
    setLoading(true);
    setStatus('Searching for user...');

    try {
      // Find user with the specified email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setStatus('No user found with that email.');
        setLoading(false);
        return;
      }

      let userId = null;
      let username = null;

      snapshot.forEach(userDoc => {
        userId = userDoc.id;
        username = userDoc.data().username || 'Unknown';
        setStatus(`Found user: ${username}`);
      });

      if (userId) {
        setStatus('Adding admin privileges...');

        // Add to admins collection
        await setDoc(doc(db, 'admins', userId), {
          username: username,
          addedAt: serverTimestamp(),
          addedBy: localStorage.getItem('userId'),
          permissions: ['ban_users', 'view_reports', 'send_mass_messages']
        });

        setStatus(`SUCCESS! ${username} is now an admin.`);
      }
    } catch (error) {
      setStatus('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>Loading...</div>;
  }

  if (!isAuthorized) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ background: 'white', padding: '3rem', borderRadius: '16px', textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        background: 'white',
        padding: '3rem',
        borderRadius: '16px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        <h1 style={{ margin: '0 0 1rem 0', color: '#333' }}>Admin Setup</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Add a new admin. Only existing admins can use this page.
        </p>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#555', fontWeight: 600 }}>
            Email Address:
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '1rem',
              outline: 'none'
            }}
            placeholder="Enter user email"
          />
        </div>

        <button
          onClick={setupAdmin}
          disabled={loading || !email}
          style={{
            width: '100%',
            padding: '1rem',
            background: loading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '1.5rem'
          }}
        >
          {loading ? 'Setting up...' : 'Add Admin'}
        </button>

        {status && (
          <div style={{
            padding: '1rem',
            background: status.includes('Error') ? '#ffebee' : status.includes('SUCCESS') ? '#e8f5e9' : '#fff3e0',
            borderRadius: '8px',
            border: `2px solid ${status.includes('Error') ? '#f44336' : status.includes('SUCCESS') ? '#4caf50' : '#ff9800'}`,
            whiteSpace: 'pre-wrap',
            fontSize: '0.95rem',
            color: '#333'
          }}>
            {status}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSetup;
